import React, { useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Chart } from 'react-google-charts'
import Spinner from '../../../util/spinner-shim'
import { chartDimensions } from 'altrac-conversions'

import AppDispatcher from '../../../dispatchers/AppDispatcher'

import NoChartData from './NoChartData'
import styles from '../../../../css/charts.module.css'
import ChartDataLoading from './ChartDataLoading'

function makeColumns () {
  return ([
    {
      type: 'date',
      label: 'Time',
    },
    {
      type: 'number',
      label: '1',
    },
    {
      type: 'number',
      label: '2',
    },
    {
      type: 'number',
      label: '3',
    },
    {
      type: 'number',
      label: '4',
    },
  ])
}

function makeRows (sensorData, powered, debounce) {
  const rows = []
  if (
    sensorData.length > 0
  ) {
    let dataToPush = []
    for (let i = 0; i < sensorData.length; i += 1) {
      /*
        This is where we are going to debounce the graph page

          if a value is the same as the last value, then

      */
      let dataCurrent = sensorData[i]['131']
      if (powered) {
        dataCurrent >>>= 1
      }
      if (!debounce) {
        dataToPush = [
          new Date(sensorData[i].date),
          dataCurrent & 1 ? 1 : 0,
          dataCurrent & 2 ? 1 : 0,
          dataCurrent & 4 ? 1 : 0,
          dataCurrent & 8 ? 1 : 0,
        ]
        rows.push(dataToPush)
      } else if ( // the data matches a previous or future event, then its good
        (
          sensorData[i]['132'] &&
          i < sensorData.length - 1 &&
          i > 0
        ) ||
        i === 0
      ) {
        let dataNext = sensorData[i + 1]['131']
        if (powered) {
          dataNext >>>= 1
        }
        let dataLast
        if (i !== 0) {
          dataLast = sensorData[i - 1]['131']
          if (powered) {
            dataLast >>>= 1
          }
        }
        if (i === 0) {
          if (
            dataCurrent < dataNext ||
            dataCurrent === dataNext
          ) {
            dataToPush = [
              new Date(sensorData[i].date),
              dataCurrent & 1 ? 1 : 0,
              dataCurrent & 2 ? 1 : 0,
              dataCurrent & 4 ? 1 : 0,
              dataCurrent & 8 ? 1 : 0,
            ]
            rows.push(dataToPush)
          } else {
            dataToPush = [
              new Date(sensorData[i].date),
              dataNext & 1 ? 1 : 0,
              dataNext & 2 ? 1 : 0,
              dataNext & 4 ? 1 : 0,
              dataNext & 8 ? 1 : 0,
            ]
            rows.push(dataToPush)
          }
        } else if (
          sensorData[i]['132'] === sensorData[i + 1]['132'] ||
          sensorData[i]['132'] === sensorData[i - 1]['132']
        ) {
          dataToPush = [
            new Date(sensorData[i].date),
            dataCurrent & 1 ? 1 : 0,
            dataCurrent & 2 ? 1 : 0,
            dataCurrent & 4 ? 1 : 0,
            dataCurrent & 8 ? 1 : 0,
          ]
          rows.push(dataToPush)
        } else if (
          dataCurrent < dataNext ||
          dataCurrent < dataLast
        ) {
          dataToPush = [
            new Date(sensorData[i].date),
            dataCurrent & 1 ? 1 : 0,
            dataCurrent & 2 ? 1 : 0,
            dataCurrent & 4 ? 1 : 0,
            dataCurrent & 8 ? 1 : 0,
          ]
          rows.push(dataToPush)
        } else {
          const newDataToPush = [
            new Date(sensorData[i].date),
            dataToPush[1],
            dataToPush[2],
            dataToPush[3],
            dataToPush[4],
          ]
          rows.push(newDataToPush)
        }
      } else if (sensorData[i]['132']) { // data has 132
        const newDataToPush = [
          new Date(sensorData[i].date),
          dataToPush[1],
          dataToPush[2],
          dataToPush[3],
          dataToPush[4],
        ]
        rows.push(newDataToPush)
      } else { // its an old device, so no 132
        dataToPush = [
          new Date(sensorData[i].date),
          dataCurrent & 1 ? 1 : 0,
          dataCurrent & 2 ? 1 : 0,
          dataCurrent & 4 ? 1 : 0,
          dataCurrent & 8 ? 1 : 0,
        ]
        rows.push(dataToPush)
      }
    }
  }
  return rows
}

const Cell = function Cell ({ value }) {
  let returnVal
  if (value === 1) {
    returnVal = (
      <td>
        <i className='fa fa-circle' />
      </td>
    )
  } else if (value === 0) {
    returnVal = (
      <td>
        <i className='fa fa-circle-o' />
      </td>
    )
  } else {
    returnVal = (
      <td>
        {value}
      </td>
    )
  }
  return returnVal
}

Cell.propTypes = {
  value: PropTypes.node,
}

const Row = function Row ({ data }) {
  const cells = []
  for (let i = 0; i < data.length; i += 1) {
    let cellValue
    if (typeof data[i].getMonth === 'function') {
      cellValue = data[i].toLocaleString()
    } else {
      cellValue = data[i]
    }
    cells.push(
      <Cell key={i} value={cellValue} />
    )
  }
  return (
    <tr>
      {cells}
    </tr>
  )
}

Row.propTypes = {
  data: PropTypes.array,
}

const Table = function Table ({ sensorData, device }) {
  if (sensorData.length < 1) {
    return (
      <div className='loading'>
        <Spinner name='double-bounce' />
      </div>
    )
  }

  let powered = false
  let debounce = false
  if (
    device &&
    device.physical
  ) {
    if (device.physical.powered) {
      powered = device.physical.powered
    }
    if (device.physical.debounce) {
      debounce = device.physical.debounce
    }
  }
  const rows = makeRows(sensorData, powered, debounce)
  const rowsArr = []
  for (let i = 0; i < rows.length; i += 1) {
    rowsArr.push(
      <Row key={i} data={rows[i]} />
    )
  }
  const columns = makeColumns()
  const columnArr = []
  for (let i = 0; i < columns.length; i += 1) {
    columnArr.push(columns[i].label)
  }
  return (
    <table className='table table-striped'>
      <thead>
        <Row data={columnArr} />
      </thead>
      <tbody>
        {rowsArr}
      </tbody>
    </table>
  )
}

Table.propTypes = {
  sensorData: PropTypes.arrayOf(PropTypes.object),
}

export const ChartBinDicator = function ChartBinDicator ({ sensorData, metricReady, device }) {
  if (!metricReady) {
    return (
      <NoChartData />
    )
  }

  const options = {
    title: 'Bindicators Review',
    hAxis: {
      slantedText: false,
      gridlines: {
        count: -1,
        units: {
          days: { format: ['M/d'] },
          hours: { format: ['M/d ha'] },
        },
      },
      minorGridlines: {
        units: {
          hours: { format: ['ha'] },
          minutes: { format: ['HH:mm a Z', ':mm'] },
        },
      },
    },
    vAxis: {
      title: 'Bin Levels',
      minValue: 0,
      maxValue: 4,
    },
    chartArea: {
      left: 0,
      top: 0,
      width: '100%',
      height: '85%',
    },
    isStacked: true,
    legend: { position: 'bottom' },
  }

  let powered = false
  let debounce = false
  if (
    device &&
    device.physical
  ) {
    if (device.physical.powered) {
      powered = device.physical.powered
    }
    if (device.physical.debounce) {
      debounce = device.physical.debounce
    }
  }
  const rows = makeRows(sensorData, powered, debounce)
  if (rows.length < 1) {
    return (
      <NoChartData />
    )
  }

  const columns = makeColumns()

  const dimensions = chartDimensions(window.innerWidth)

  return (
    <div className={styles.chart}>
      <Chart
        chartType='AreaChart'
        rows={rows}
        width={dimensions.width}
        height={dimensions.height}
        columns={columns}
        options={options}
        style={{
          margin: '0 auto',
          position: 'relative',
          top: '5px',
        }}
      />
    </div>
  )
}

ChartBinDicator.propTypes = {
  sensorData: PropTypes.arrayOf(PropTypes.object),
  metricReady: PropTypes.bool,
  device: PropTypes.object,
}

function TelemetryBinDicators ({
  device, periodStart, periodStop, sensorData, metricReady,
}) {
  const getNewData = useCallback((address, periodStartParam, periodStopParam) => {
    AppDispatcher.dispatch({
      action: 'get-metric-address',
      address: address || device.address,
      timeBeg: periodStartParam || periodStart,
      timeEnd: periodStopParam || periodStop,
    })
  }, [device.address, periodStart, periodStop])

  useEffect(() => {
    getNewData()
  }, [getNewData])

  useEffect(() => {
    const interval = setInterval(getNewData, 60000)
    return () => {
      clearInterval(interval)
    }
  }, [getNewData])

  useEffect(() => {
    getNewData(null, periodStart, periodStop)
  }, [periodStart, periodStop, getNewData])

  if (
    !Array.isArray(sensorData) ||
    sensorData.length < 1 ||
    !metricReady
  ) {
    return (
      <ChartDataLoading />
    )
  }
  return (
    <div className='deviceInfo'>
      <ChartBinDicator
        sensorData={sensorData}
        metricReady={metricReady}
        device={device}
      />
      <Table
        sensorData={sensorData}
        device={device}
      />
    </div>
  )
}

TelemetryBinDicators.propTypes = {
  device: PropTypes.object,
  periodStart: PropTypes.string,
  periodStop: PropTypes.string,
  sensorData: PropTypes.array,
  metricReady: PropTypes.bool,
}

export default TelemetryBinDicators
